import React from 'react';
import { Trans } from '@lingui/macro';
import { I18n } from '@lingui/react';
import { formatDate, formatNumber } from '@oms/utils';
import { Box } from '@oms/ui-box';
import { Button } from '@oms/ui-button';
import { DateInput, registerLocale, nb, sv } from '@oms/ui-date-input';
import { TextInput } from '@oms/ui-text-input';
import { ModuleSection, ModuleHeading, ModuleProps } from 'components/Module';
import { useCustomerByToken } from 'client/customers';
import { useCalculateProfit } from 'client/calculate-profit';
import { getTrendColor } from 'utils/trendColor';
import { useForm, getInitialDate, getDateFormatString } from './utils';
import { Form, Key, Label, Result, Value } from './styles';
import { useLanguage } from '@oms/ui-language-context';

registerLocale('no', nb);
registerLocale('sv', sv);

export default function Calculator({
  token,
  standalone = true,
  ...styleProps
}: ModuleProps) {
  const customer = useCustomerByToken(token);
  const lang = useLanguage();
  const [expand, setState] = React.useState<'open' | 'closed'>('closed');
  const setExpand = (state: 'open' | 'closed') => {
    if (standalone) {
      setState(state);
    }
  };

  const form = useForm({
    numberOfShares: 1000,
    fromDate: getInitialDate(customer?.notingdate as any),
  });

  const enabled =
    new Date(customer?.notingdate as any).toDateString() !==
    new Date().toDateString();

  const { data, refetch, ...fetchState } = useCalculateProfit({
    token,
    numberOfShares: form.numberOfShares,
    fromDate: form.fromDate,
    enabled,
  });

  const trendColor = getTrendColor(data?.earnings);
  const disabledBefore = customer?.notingdate
    ? new Date(customer?.notingdate)
    : undefined;

  return (
    <ModuleSection
      {...styleProps}
      mb={(expand === 'open' ? '15rem' : 'initial') as any}
    >
      {standalone ? null : (
        <ModuleHeading>
          <Trans>Profit calculator</Trans>
        </ModuleHeading>
      )}
      <Box border="sm" borderColor="border">
        <Box
          display="flex"
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          borderBottom="sm"
          borderColor="border"
          minHeight={20}
          p={4}
        >
          <Box display="flex" justifyContent="center" mt={{ base: 4, md: 0 }}>
            {/** return */}
            <Box mr={2} flex={1} justifyContent="space-between">
              <Key>
                <Trans>Return (incl. dividends)</Trans>
              </Key>
              <Value color={trendColor}>
                {formatNumber(data?.returnPercentageIncludingDividends)}%
              </Value>
            </Box>
            {/** last dividend */}
            <Box ml={2} mr={2} flex={1}>
              <Key textAlign={{ base: 'center', md: 'initial' }}>
                <Trans>Last dividend</Trans>
              </Key>
              <Value textAlign={{ base: 'center', md: 'initial' }}>
                <I18n>
                  {({ i18n }) =>
                    data?.lastDividendDate
                      ? formatDate(
                          data?.lastDividendDate,
                          getDateFormatString(i18n.language),
                        )
                      : '-'
                  }
                </I18n>
              </Value>
            </Box>
            {/** dividend */}
            <Box ml={2} flex={1}>
              <Key textAlign={{ base: 'right', md: 'initial' }}>
                <Trans>Dividend</Trans>
              </Key>
              <Value textAlign={{ base: 'right', md: 'initial' }}>
                {formatNumber(data?.dividends)}
              </Value>
            </Box>
          </Box>
          {/** result */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems={{ base: 'center', md: 'flex-end' }}
            justifyContent="space-between"
            flex={1}
          >
            <Key mb={2}>
              <Trans>Result</Trans>
            </Key>

            <Box
              display="flex"
              justifyContent="center"
              alignItems="flex-end"
              pb={{ base: 4, md: 0 }}
              mb={{ base: 2, md: 0 }}
              borderBottom={{ base: 'sm', md: 'none' }}
              borderColor="border"
              width={{ base: '100%', md: 'auto' }}
            >
              <Result mr={1} color={trendColor}>
                {formatNumber(data?.earnings)}
              </Result>
            </Box>
          </Box>
        </Box>
        <Form
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (enabled) {
              refetch();
            }
          }}
        >
          <Box display="flex" flexDirection="column" flex={1} px={{ md: 2 }}>
            <Label htmlFor="numberOfShares" mb={2}>
              <Trans>No. of shares</Trans>
            </Label>
            <TextInput
              id="numberOfShares"
              type="number"
              min={0}
              step={1}
              value={form.numberOfShares}
              onChange={form.onChangeShares}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            px={{ md: 2 }}
            textTransform="capitalize"
          >
            <Label htmlFor="fromDate" mb={2}>
              <Trans>Purchase date</Trans>
            </Label>
            <DateInput
              id="fromDate"
              value={form.fromDate}
              minDate={disabledBefore}
              onChange={form.onChangeDate}
              onCalendarOpen={() => setExpand('open')}
              onCalendarClose={() => setExpand('closed')}
              locale={lang === 'en' ? undefined : lang}
            />
          </Box>

          <Button flex="1 0 auto" type="submit" disabled={!enabled}>
            <Trans>Calculate</Trans>
          </Button>
        </Form>
        {fetchState.error && (
          <Box display="flex" px={4} mt={-4} pb={4} color="neg" role="alert">
            <Trans>
              Something went wrong while getting data. Please try again.
            </Trans>
          </Box>
        )}
      </Box>
    </ModuleSection>
  );
}

export { Calculator };
